import { useMemo } from "react";
import Drawer from "../Drawer";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  applyDemandPlanningDefaultColumnVisibility,
  groupDemandPlanningColumnSettings,
} from "../../consts/demandplanning";

export const DemandPlanningColumnsSettings = ({
  marginFromSidebar,
  isOpen,
  onClose,
  viewName,
  viewId,
  planningYear = null,
  presetColumnIds,
  columns,
  onChangeColumns,
  onSaveCurrentView,
}) => {
  const handleToggle = (id) => {
    onChangeColumns((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (c.pinned) return c;
        return { ...c, visible: !c.visible };
      })
    );
  };

  const handleDefault = () => {
    onChangeColumns((prev) =>
      applyDemandPlanningDefaultColumnVisibility(prev, viewId, presetColumnIds, planningYear)
    );
  };

  const handleShowAll = () => {
    onChangeColumns((prev) =>
      prev.map((c) => ({ ...c, visible: true }))
    );
  };

  const groupedColumns = useMemo(
    () => groupDemandPlanningColumnSettings(columns),
    [columns]
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Column visibility"
      width="500px"
      marginFromSidebar={marginFromSidebar}
      footer={
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onSaveCurrentView}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-primary shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <PlusIcon className="h-5 w-5" />
            Save current view
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-5 px-6 py-5">
        <p className="text-sm text-gray-600">
          Show or hide columns for{" "}
          <span className="font-medium text-gray-900">{viewName || "this view"}</span>.
          Pinned columns stay visible for row context.
        </p>
        <div className="flex flex-row gap-2">
          <button
            type="button"
            onClick={handleDefault}
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Default
          </button>
          <button
            type="button"
            onClick={handleShowAll}
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Show all
          </button>
        </div>

        {!columns?.length ? (
          <p className="text-sm text-gray-500">
            No columns are defined for this planning view.
          </p>
        ) : (
          <div className="flex flex-col gap-5 pb-2">
            {groupedColumns.map((group) => (
              <section key={group.groupId}>
                <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                  {group.label}
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {group.columns.map((col) => {
                    const isPinned = col.pinned === true;
                    return (
                      <li
                        key={col.id}
                        className="flex flex-row items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-gray-50"
                      >
                        <label
                          className={`flex min-w-0 flex-1 flex-row items-center gap-3 ${
                            isPinned ? "cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={col.visible}
                            disabled={isPinned}
                            onChange={() => handleToggle(col.id)}
                            className="h-4 w-4 rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-70"
                          />
                          <span className="truncate text-sm font-medium text-gray-900">
                            {col.label}
                          </span>
                        </label>
                        {isPinned && (
                          <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            pinned
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default DemandPlanningColumnsSettings;
